import { STEPS, asText } from '../utils/templateForm';

function JsonBlock({ title, value }) {
  return (
    <div style={card}>
      <div style={sectionTitle}>{title}</div>
      <pre style={pre}>{JSON.stringify(value, null, 2)}</pre>
    </div>
  );
}

export default function TemplateWizardFields({
  form,
  updateField,
  updateVariable,
  addVariable,
  removeVariable,
  activeStep,
  setActiveStep,
  sourceTestDeviceId,
  setSourceTestDeviceId,
  sourceValidationResult,
  onValidateSource,
  validatingSource,
  onSave,
  saving,
  saveLabel,
}) {
  const activeStepIndex = STEPS.findIndex((step) => step.id === activeStep);

  return (
    <>
      <div style={wizardTabs}>
        {STEPS.map((step, index) => (
          <button
            key={step.id}
            style={{
              ...wizardTab,
              background: activeStep === step.id ? '#0f766e' : '#ffffff',
              color: activeStep === step.id ? '#ffffff' : '#0f172a',
              borderColor: activeStep === step.id ? '#0f766e' : '#dbe4f0',
            }}
            onClick={() => setActiveStep(step.id)}
          >
            <span style={wizardIndex}>{index + 1}</span>
            {step.title}
          </button>
        ))}
      </div>

      {activeStep === 'basics' && (
        <>
          <div style={cardGrid}>
            <div style={card}>
              <div style={sectionTitle}>Template Basics</div>
              <div style={fieldGrid}>
                <label style={field}>
                  <span style={label}>Template Name</span>
                  <input style={input} value={form.name} onChange={(e) => updateField('name', e.target.value)} />
                </label>
                <label style={field}>
                  <span style={label}>Data Source Type</span>
                  <select style={input} value={form.source_type} onChange={(e) => updateField('source_type', e.target.value)}>
                    <option value="http">HTTP Request</option>
                    <option value="mongodb">MongoDB Document</option>
                  </select>
                </label>
                <label style={field}>
                  <span style={label}>HTTP Method</span>
                  <select style={input} value={form.method} onChange={(e) => updateField('method', e.target.value)} disabled={form.source_type !== 'http'}>
                    <option value="GET">GET</option>
                    <option value="POST">POST</option>
                  </select>
                </label>
                <label style={field}>
                  <span style={label}>Sample Device ID For Source Check</span>
                  <input
                    style={input}
                    placeholder="WMSC0021"
                    value={sourceTestDeviceId}
                    onChange={(e) => setSourceTestDeviceId(e.target.value)}
                  />
                </label>
                <label style={field}>
                  <span style={label}>Batch Size</span>
                  <input type="number" min="1" style={input} value={form.batch_size} onChange={(e) => updateField('batch_size', e.target.value)} />
                </label>
                <label style={{ ...field, justifyContent: 'flex-end' }}>
                  <span style={label}>Template Active</span>
                  <input type="checkbox" checked={form.is_active} onChange={(e) => updateField('is_active', e.target.checked)} style={{ width: 18, height: 18 }} />
                </label>
                <label style={{ ...field, gridColumn: '1 / -1' }}>
                  <span style={label}>Description</span>
                  <textarea style={{ ...input, minHeight: 84 }} value={form.description} onChange={(e) => updateField('description', e.target.value)} />
                </label>

                {form.source_type === 'http' ? (
                  <label style={{ ...field, gridColumn: '1 / -1' }}>
                    <span style={label}>Data Source URL</span>
                    <input
                      style={input}
                      placeholder="https://api.thingzcloud.com/devices/data/{device_id}"
                      value={form.data_source_url}
                      onChange={(e) => updateField('data_source_url', e.target.value)}
                    />
                  </label>
                ) : (
                  <>
                    <label style={{ ...field, gridColumn: '1 / -1' }}>
                      <span style={label}>MongoDB Connection String</span>
                      <input
                        style={input}
                        placeholder="mongodb+srv://user:pass@cluster.mongodb.net/"
                        value={form.mongodb_uri}
                        onChange={(e) => updateField('mongodb_uri', e.target.value)}
                      />
                    </label>
                    <label style={field}>
                      <span style={label}>Database</span>
                      <input style={input} value={form.mongodb_database} onChange={(e) => updateField('mongodb_database', e.target.value)} />
                    </label>
                    <label style={field}>
                      <span style={label}>Collection</span>
                      <input style={input} value={form.mongodb_collection} onChange={(e) => updateField('mongodb_collection', e.target.value)} />
                    </label>
                    <label style={field}>
                      <span style={label}>Latest Row Sort Field</span>
                      <input style={input} placeholder="_id" value={form.mongodb_sort_field} onChange={(e) => updateField('mongodb_sort_field', e.target.value)} />
                    </label>
                  </>
                )}
              </div>

              <div style={{ marginTop: 14, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  style={secondaryButton}
                  onClick={onValidateSource}
                  disabled={
                    validatingSource ||
                    (form.source_type === 'http' && !asText(form.data_source_url).trim()) ||
                    (form.source_type === 'mongodb' && (!asText(form.mongodb_uri).trim() || !asText(form.mongodb_database).trim() || !asText(form.mongodb_collection).trim()))
                  }
                >
                  Check Source
                </button>
                <div style={{ color: '#64748b', fontSize: 13 }}>
                  Verify the source before saving the template.
                </div>
              </div>
            </div>

            <div style={card}>
              <div style={sectionTitle}>Data Source Config</div>
              {form.source_type === 'http' ? (
                <>
                  <div style={fieldGrid}>
                    <label style={field}>
                      <span style={label}>Headers JSON</span>
                      <textarea style={{ ...input, minHeight: 110, fontFamily: 'monospace' }} value={form.headersText} onChange={(e) => updateField('headersText', e.target.value)} />
                    </label>
                    <label style={field}>
                      <span style={label}>Query Params JSON</span>
                      <textarea style={{ ...input, minHeight: 110, fontFamily: 'monospace' }} value={form.queryParamsText} onChange={(e) => updateField('queryParamsText', e.target.value)} />
                    </label>
                    <label style={{ ...field, gridColumn: '1 / -1' }}>
                      <span style={label}>Request Body JSON</span>
                      <textarea style={{ ...input, minHeight: 120, fontFamily: 'monospace' }} value={form.requestBodyText} onChange={(e) => updateField('requestBodyText', e.target.value)} />
                    </label>
                  </div>
                  <div style={exampleBox}>
                    <div style={exampleTitle}>Reusable device-id example</div>
                    <div style={exampleLine}>Actual URL now:</div>
                    <pre style={exampleCode}>https://api.thingzcloud.com/devices/data/WMSC0021</pre>
                    <div style={exampleLine}>Template URL to save:</div>
                    <pre style={exampleCode}>https://api.thingzcloud.com/devices/data/{'{device_id}'}</pre>
                    <div style={exampleNote}>
                      When a target device has <code>device_id = WMSC0021</code>, the system replaces <code>{'{device_id}'}</code> automatically.
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <label style={{ ...field, marginBottom: 14 }}>
                    <span style={label}>MongoDB Filter JSON</span>
                    <textarea style={{ ...input, minHeight: 160, fontFamily: 'monospace' }} value={form.mongodb_filterText} onChange={(e) => updateField('mongodb_filterText', e.target.value)} />
                  </label>
                  <div style={exampleBox}>
                    <div style={exampleTitle}>MongoDB latest document mode</div>
                    <div style={exampleLine}>The system connects to MongoDB, applies the filter, sorts descending, and fetches the last inserted or latest matching row.</div>
                    <div style={exampleLine}>You can still use <code>{'{device_id}'}</code> inside the filter or even inside the connection string if you need a device-specific lookup.</div>
                    <pre style={exampleCode}>{`{
  "device_id": "{device_id}"
}`}</pre>
                    <div style={exampleNote}>
                      In Step 2, each mapped variable pulls its value from that latest MongoDB document.
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {sourceValidationResult && (
            <div style={cardGrid}>
              <div style={card}>
                <div style={sectionTitle}>Source Check Result</div>
                <div
                  style={{
                    ...statusPill,
                    background: sourceValidationResult.success ? '#dcfce7' : '#fee2e2',
                    color: sourceValidationResult.success ? '#166534' : '#991b1b',
                  }}
                >
                  {sourceValidationResult.success ? 'Working' : 'Failed'}
                </div>
                <div style={previewLabel}>
                  {sourceValidationResult.sourceType === 'mongodb' ? 'Resolved Collection' : 'Resolved URL'}
                </div>
                <pre style={pre}>
                  {sourceValidationResult.sourceType === 'mongodb'
                    ? sourceValidationResult.resolvedCollection || form.mongodb_collection || '-'
                    : sourceValidationResult.resolvedUrl || form.data_source_url || '-'}
                </pre>
                {sourceValidationResult.sourceType === 'mongodb' && (
                  <>
                    <div style={previewLabel}>Resolved Filter</div>
                    <pre style={pre}>{JSON.stringify(sourceValidationResult.resolvedFilter || {}, null, 2)}</pre>
                  </>
                )}
                <div style={previewLabel}>Status</div>
                <div style={previewBox}>{sourceValidationResult.statusCode ?? 'No response code'}</div>
                {sourceValidationResult.error && (
                  <>
                    <div style={previewLabel}>Error</div>
                    <pre style={pre}>{sourceValidationResult.error}</pre>
                  </>
                )}
              </div>
              <JsonBlock
                title={sourceValidationResult.success ? 'Source Check: Raw Data' : 'Source Check: Response/Error Body'}
                value={sourceValidationResult.rawResponse}
              />
            </div>
          )}
        </>
      )}

      {activeStep === 'variables' && (
        <div style={card}>
          <div style={sectionTitle}>Variable Mapping</div>
          <div style={{ display: 'grid', gap: 10 }}>
            {form.variableRows.map((row, index) => (
              <div key={index} style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 10 }}>
                <div style={mappingRow}>
                  <input style={input} placeholder="variable_name" value={row.key} onChange={(e) => updateVariable(index, 'key', e.target.value)} />
                  <input style={input} placeholder="json.path.to.value" value={row.path} onChange={(e) => updateVariable(index, 'path', e.target.value)} />
                  <button style={ghostButton} onClick={() => removeVariable(index)}>Remove</button>
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <select
                    style={{ ...input, width: 'auto', minWidth: 150 }}
                    value={row.format || 'none'}
                    onChange={(e) => updateVariable(index, 'format', e.target.value)}
                  >
                    <option value="none">Raw value</option>
                    <option value="timestamp">Format as timestamp</option>
                  </select>
                  {row.format === 'timestamp' && (
                    <>
                      <input
                        style={{ ...input, width: 'auto', minWidth: 180 }}
                        placeholder="YYYY-MM-DD HH:mm:ss"
                        value={row.timestampFormat}
                        onChange={(e) => updateVariable(index, 'timestampFormat', e.target.value)}
                      />
                      <input
                        style={{ ...input, width: 'auto', minWidth: 160 }}
                        placeholder="Timezone (e.g. Asia/Kolkata)"
                        value={row.timezone}
                        onChange={(e) => updateVariable(index, 'timezone', e.target.value)}
                      />
                    </>
                  )}
                </div>
              </div>
            ))}
            <div>
              <button style={secondaryButton} onClick={addVariable}>Add Variable</button>
            </div>
          </div>
          <div style={{ marginTop: 12, color: '#64748b', fontSize: 13 }}>
            Built-in variables always available: `device_id`, `device_name`, `location`, `recipient_name`, `email`, `phone_number`, `date`, `time`.
            Set "Format as timestamp" on a variable to convert the raw fetched value (Unix time, ISO string, etc.) into a custom date/time format
            (tokens: YYYY, MM, DD, HH, mm, ss) in an optional timezone before it's substituted into the message.
          </div>
        </div>
      )}

      {activeStep === 'messages' && (
        <div style={cardGrid}>
          <div style={card}>
            <div style={sectionTitle}>Email Template</div>
            <label style={field}>
              <span style={label}>Subject</span>
              <input style={input} value={form.email_subject} onChange={(e) => updateField('email_subject', e.target.value)} />
            </label>
            <label style={{ ...field, marginTop: 12 }}>
              <span style={label}>Body</span>
              <textarea style={{ ...input, minHeight: 220 }} value={form.email_template} onChange={(e) => updateField('email_template', e.target.value)} placeholder="Hello {{recipient_name}}, COD is {{cod}} for device {{device_id}}." />
            </label>
          </div>

          <div style={card}>
            <div style={sectionTitle}>WhatsApp Template</div>
            <label style={field}>
              <span style={label}>Mode</span>
              <select style={input} value={form.whatsapp_mode} onChange={(e) => updateField('whatsapp_mode', e.target.value)}>
                <option value="text">Plain Text</option>
                <option value="api_template">Approved API Template</option>
              </select>
            </label>
            {form.whatsapp_mode === 'api_template' ? (
              <>
                <label style={{ ...field, marginTop: 12 }}>
                  <span style={label}>Template Name</span>
                  <input
                    style={input}
                    value={form.whatsapp_template_name}
                    onChange={(e) => updateField('whatsapp_template_name', e.target.value)}
                    placeholder="approved_template_name"
                  />
                </label>
                <label style={{ ...field, marginTop: 12 }}>
                  <span style={label}>Language Code</span>
                  <input
                    style={input}
                    value={form.whatsapp_template_lang}
                    onChange={(e) => updateField('whatsapp_template_lang', e.target.value)}
                    placeholder="en"
                  />
                </label>
                <label style={{ ...field, marginTop: 12 }}>
                  <span style={label}>Parameter Order JSON</span>
                  <textarea
                    style={{ ...input, minHeight: 150, fontFamily: 'monospace' }}
                    value={form.whatsapp_paramText}
                    onChange={(e) => updateField('whatsapp_paramText', e.target.value)}
                    placeholder={`[
  "totalizer",
  "18:00",
  "{{device_name}} ({{location}})",
  "device_id"
]`}
                  />
                </label>
                <div style={{ marginTop: 12, color: '#64748b', fontSize: 13 }}>
                  Use the variable keys in the exact order expected by the approved WhatsApp template placeholders <code>{'{{1}}'}</code>, <code>{'{{2}}'}</code>, <code>{'{{3}}'}</code>.
                  You can enter either valid JSON or a simple comma-separated list like <code>totalizer, 18:00, flow_rate, device_id</code>.
                  <br />
                  To merge several variables into a single placeholder, write an entry containing <code>{'{{var}}'}</code> expressions,
                  e.g. <code>{'{{device_name}} ({{location}})'}</code> — use the JSON array form when doing this, since plain comma-separated entries split on commas.
                </div>
              </>
            ) : (
              <label style={{ ...field, marginTop: 12 }}>
                <span style={label}>Message</span>
                <textarea style={{ ...input, minHeight: 278 }} value={form.whatsapp_template} onChange={(e) => updateField('whatsapp_template', e.target.value)} placeholder="Device {{device_id}} update: flow rate {{flow_rate}}." />
              </label>
            )}
          </div>
        </div>
      )}

      <div style={wizardFooter}>
        <button
          style={secondaryButton}
          onClick={() => setActiveStep(STEPS[Math.max(0, activeStepIndex - 1)].id)}
          disabled={activeStepIndex === 0}
        >
          Previous
        </button>
        <div style={{ color: '#64748b', fontSize: 13 }}>
          Step {activeStepIndex + 1} of {STEPS.length}
        </div>
        {activeStepIndex === STEPS.length - 1 ? (
          <button style={primaryButton} onClick={onSave} disabled={saving}>
            {saving ? 'Saving…' : saveLabel}
          </button>
        ) : (
          <button
            style={primaryButton}
            onClick={() => setActiveStep(STEPS[Math.min(STEPS.length - 1, activeStepIndex + 1)].id)}
          >
            Next
          </button>
        )}
      </div>
    </>
  );
}

export const cardGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  gap: 18,
};

export const card = {
  background: '#ffffff',
  border: '1px solid #dbe4f0',
  borderRadius: 20,
  padding: 20,
  boxShadow: '0 10px 30px rgba(15, 23, 42, 0.04)',
};

const wizardTabs = {
  display: 'flex',
  gap: 10,
  flexWrap: 'wrap',
};

const wizardTab = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  border: '1px solid #dbe4f0',
  borderRadius: 999,
  padding: '10px 16px',
  cursor: 'pointer',
  fontWeight: 700,
};

const wizardIndex = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24,
  borderRadius: '50%',
  background: 'rgba(255,255,255,0.2)',
  border: '1px solid currentColor',
  fontSize: 12,
};

const wizardFooter = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 12,
  padding: '12px 4px',
};

export const sectionTitle = {
  fontSize: 18,
  fontWeight: 700,
  color: '#0f172a',
  marginBottom: 14,
};

const fieldGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: 14,
};

export const field = {
  display: 'grid',
  gap: 6,
};

export const label = {
  color: '#334155',
  fontSize: 13,
  fontWeight: 600,
};

export const input = {
  width: '100%',
  boxSizing: 'border-box',
  border: '1px solid #cbd5e1',
  borderRadius: 12,
  padding: '10px 12px',
  fontSize: 14,
  color: '#0f172a',
  background: '#fff',
};

export const primaryButton = {
  border: 'none',
  borderRadius: 999,
  padding: '10px 18px',
  background: '#0f766e',
  color: '#fff',
  fontWeight: 700,
  cursor: 'pointer',
};

export const secondaryButton = {
  border: '1px solid #94a3b8',
  borderRadius: 999,
  padding: '10px 18px',
  background: '#fff',
  color: '#0f172a',
  fontWeight: 700,
  cursor: 'pointer',
};

const ghostButton = {
  border: '1px solid #cbd5e1',
  borderRadius: 10,
  padding: '10px 12px',
  background: '#f8fafc',
  color: '#334155',
  cursor: 'pointer',
};

const mappingRow = {
  display: 'grid',
  gridTemplateColumns: 'minmax(160px, 1fr) minmax(220px, 2fr) auto',
  gap: 10,
};

const previewBox = {
  border: '1px solid #dbe4f0',
  borderRadius: 12,
  background: '#f8fafc',
  padding: 12,
  color: '#0f172a',
  marginBottom: 12,
};

const previewLabel = {
  margin: '8px 0 6px',
  fontSize: 13,
  fontWeight: 700,
  color: '#334155',
};

export const pre = {
  margin: 0,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  background: '#f8fafc',
  border: '1px solid #dbe4f0',
  borderRadius: 12,
  padding: 12,
  color: '#0f172a',
  fontSize: 13,
  overflow: 'auto',
};

const exampleBox = {
  marginTop: 14,
  border: '1px solid #bfdbfe',
  borderRadius: 16,
  background: '#eff6ff',
  padding: 16,
};

const exampleTitle = {
  color: '#1d4ed8',
  fontWeight: 700,
  marginBottom: 8,
};

const exampleLine = {
  color: '#334155',
  fontSize: 13,
  marginBottom: 4,
};

const exampleCode = {
  margin: '0 0 10px',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  background: '#ffffff',
  border: '1px solid #bfdbfe',
  borderRadius: 12,
  padding: 12,
  color: '#0f172a',
  fontSize: 13,
};

const exampleNote = {
  color: '#475569',
  fontSize: 13,
  lineHeight: 1.5,
};

const statusPill = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '6px 10px',
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 700,
  marginBottom: 12,
};
